/**
 * InexedDB 사용을 위한 추상 객체
 * DB명 또는 테이블 명에 따라 구현체 작성
 * 구현체는 싱글톤 패턴으로 구
 */
abstract class AbstractDBService {
  private dbName: string
  private tableName: string
  private version: number

  protected constructor (_dbName: string, _tableName: string, _version: number) {
    this.dbName = _dbName
    this.tableName = _tableName
    this.version = _version
  }

  /**
   * 버전 업데이트 시 로직 구현
   * 각 버전별 조건문을 통해 로직 구현
   */
  protected abstract onUpgrade (): void

  /**
   * DB에 접근
   * DB에 해당 테이블이 없을 경우 생성
   * 버전 업데이트 로직 실행
   * DB 접근 에러 발생 시 db 제거
   * @returns
   */
  private openDB = () => {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const openReq = indexedDB.open(this.dbName, this.version)

      // DB가 존재하지 않거나 버전 업데이트 시
      openReq.onupgradeneeded = () => {
        const db = openReq.result
        // 테이블이 없을 경우 생성
        if (this.tableName && !db.objectStoreNames.contains(this.tableName)) {
          db.createObjectStore(this.tableName, { keyPath: 'id', autoIncrement: true })
        }
        // 버전 업데이트 로직 실행
        if (this.onUpgrade) {
          this.onUpgrade()
        }
      }

      // TODO: DB는 존재하지만 테이블이 존재하지 않을 경우
      openReq.onsuccess = () => {
        return resolve(openReq.result)
      }
      openReq.onerror = () => {
        this.deleteDB().then(() => reject(openReq.error))
      }
    })
  }

  /**
   * 첫번째 데이터 조회
   * @returns
   */
  findFirst = async () => {
    const db = await this.openDB()
    const tx = db.transaction(this.tableName, 'readonly')
    const store = tx.objectStore(this.tableName)

    return new Promise((resolve, reject) => {
      const req = store.getAll()

      req.onsuccess = (e) => {
        const result = (e.target as IDBRequest).result
        if (Array.isArray(result) && result.length > 0) {
          resolve((result as any[])[0])
        } else {
          resolve(null)
        }
      }

      req.onerror = () => {
        this.deleteDB().then(() => reject(req.error))
      }
    })
  }

  /**
   * 모든 데이터 조회
   * @returns
   */
  findAll = async () => {
    const db = await this.openDB()
    const tx = db.transaction(this.tableName, 'readonly')
    const store = tx.objectStore(this.tableName)

    return new Promise((resolve, reject) => {
      const req = store.getAll()

      req.onsuccess = (e) => {
        resolve((e.target as IDBRequest).result)
      }

      req.onerror = () => {
        this.deleteDB().then(() => reject(req.error))
      }
    })
  }

  /**
   * id를 통한 조회
   * @param id
   * @returns
   */
  findById = async (id: number) => {
    const db = await this.openDB()
    const tx = db.transaction(this.tableName, 'readonly')
    const store = tx.objectStore(this.tableName)

    return new Promise((resolve, reject) => {
      const req = store.get(id)

      req.onsuccess = (e) => {
        resolve((e.target as IDBRequest).result)
      }

      req.onerror = () => {
        this.deleteDB().then(() => reject(req.error))
      }
    })
  }

  /**
   * 데이터 저장
   * @param data
   * @returns
   */
  save = async (data: any) => {
    const db = await this.openDB()
    const tx = db.transaction(this.tableName, 'readwrite')
    const store = tx.objectStore(this.tableName)

    store.add(data)

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => {
        this.deleteDB().then(() => reject(tx.error))
      }
    })
  }

  /**
   * 데이터 덮어 쓰기
   * @param id
   * @param data
   * @returns
   */
  put = async (id: number, data: any) => {
    const db = await this.openDB()
    const tx = db.transaction(this.tableName, 'readwrite')
    const store = tx.objectStore(this.tableName)

    store.put({ ...data, id })

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => {
        this.deleteDB().then(() => reject(tx.error))
      }
    })
  }

  /**
   * 데이터 제거
   * @param id
   * @returns
   */
  remove = async (id: number) => {
    const db = await this.openDB()
    const tx = db.transaction(this.tableName, 'readwrite')
    const store = tx.objectStore(this.tableName)

    store.delete(id)

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => {
        this.deleteDB().then(() => reject(tx.error))
      }
    })
  }

  /**
   * 테이블 내 모든 데이터 제거
   * @returns
   */
  clearAll = async () => {
    const db = await this.openDB()
    const tx = db.transaction(this.tableName, 'readwrite')
    const store = tx.objectStore(this.tableName)

    store.clear()

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => {
        this.deleteDB().then(() => reject(tx.error))
      }
    })
  }

  /**
   * DB 제거
   * @returns
   */
  deleteDB = async () => {
    const req = indexedDB.deleteDatabase(this.dbName)

    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve('deleted')
      req.onerror = () => reject(req.error)
    })
  }
}

export default AbstractDBService
